export type ProductId = 'sofa' | 'chair' | 'sofa2' | 'chair2';
export type TabId = 'fabric' | 'finish' | 'pillow' | 'stud';

/** All possible swatch-data keys stored in Redux */
export type SwatchKey =
  | 'frameFabrics'
  | 'seatFabrics'
  | 'backCushionFabrics'
  | 'armrestFabrics'
  | 'ralphTwoFabric'
  | 'ralphChairSeatFabrics'
  | 'ralphChairBackFabrics'
  | 'ralphChairArmrestFabrics';

/** All possible finish swatch keys */
export type FinishSwatchKey = 'woodFinishes' | 'ralphSofaChairFinishes';

export interface FabricZone {
  id: string;
  label: string;
  swatchKey: SwatchKey;
  paramKey: 'frameFabricUrl' | 'seatFabricUrl' | 'backCushionFabricUrl' | 'armrestFabricUrl';
}

export interface ProductSpec {
  label: string;
  value: string;
}

export interface BaseImage {
  path: string;
  filename: string;
}

export interface PillowConfig {
  /** Whether to show the size selector (S/M/L pills) */
  showSize: boolean;
  /** Whether to show the pillow image swatch and count controls */
  showImageAndCount: boolean;
  /** Which swatch key to use for pillow fabric selection */
  fabricSwatchKey: SwatchKey;
}

export interface StudImage {
  path: string;
  label: string;
}

export interface StudConfig {
  /** Whether to show the size selector */
  showSize: boolean;
  /** Available stud images to choose from */
  images: StudImage[];
}

export interface DimensionRow {
  label: string;
  value: string;
}

export interface ProductConfig {
  id: ProductId;
  name: string;
  subtitle: string;
  tagline: string;
  heroImage: string;
  tabs: TabId[];
  fabricZones: FabricZone[];
  /** Which finish swatch key to read from Redux */
  finishSwatchKey: FinishSwatchKey;
  /** Pillow behaviour — only present when 'pillow' tab is included */
  pillowConfig?: PillowConfig;
  /** Stud behaviour — only present when 'stud' tab is included */
  studConfig?: StudConfig;
  specs: ProductSpec[];
  baseImages: BaseImage[];
  defaultDimensions: string;
  tearSheetImage: string;
  /** Tear sheet tagline displayed in the dark footer area */
  tearSheetTagline: string;
  /** Tear sheet description displayed in the dark footer area */
  tearSheetDescription: string;
  /** Structured dimensions for the tear sheet table */
  dimensionsTable: DimensionRow[];
}

export const PRODUCT_CATALOG: Record<ProductId, ProductConfig> = {
  // ── Original Sofa ──────────────────────────────────────────────────────────
  sofa: {
    id: 'sofa',
    name: 'Aran Isles Sofa',
    subtitle: 'Ralph Lauren Home',
    tagline: 'Rich upholstery with classic silhouette and premium finishing',
    heroImage: '/assets/ralph_images/sofas/demo_sofa/sofa_1.jpeg',
    tabs: ['fabric', 'finish', 'pillow'],
    fabricZones: [
      { id: 'frame', label: 'Frame', swatchKey: 'frameFabrics', paramKey: 'frameFabricUrl' },
      { id: 'seat', label: 'Cushion', swatchKey: 'seatFabrics', paramKey: 'seatFabricUrl' },
      { id: 'back', label: 'Back', swatchKey: 'backCushionFabrics', paramKey: 'backCushionFabricUrl' },
    ],
    finishSwatchKey: 'woodFinishes',
    pillowConfig: { showSize: true, showImageAndCount: true, fabricSwatchKey: 'seatFabrics' },
    specs: [
      { label: 'Overall', value: '36 1/3" H x 93 1/3" L x 40" D' },
      { label: 'Seat Width', value: '79"' },
      { label: 'Seat Depth', value: '24"' },
      { label: 'Seat Height', value: '18"' },
      { label: 'Arm Height', value: '24 1/3"' },
      { label: 'Weight', value: '200 lbs' },
    ],
    baseImages: [
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_1.jpeg', filename: 'sofa_1.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_2.jpeg', filename: 'sofa_2.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_3.jpeg', filename: 'sofa_3.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_4.jpeg', filename: 'sofa_4.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_5.jpeg', filename: 'sofa_5.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_sofa/sofa_6.jpeg', filename: 'sofa_6.jpeg' },
    ],
    defaultDimensions: '36 1/3" H x 93 1/3" L x 40" D, Seat width - 79", Seat depth - 24", Seat height - 18", Arm height - 24 1/3',
    tearSheetImage: '/assets/ralph_dimensions/sofa_tear_sheet.png',
    tearSheetTagline: 'CLASSIC COMFORT',
    tearSheetDescription: 'Casual elegance defines the Aran Isles collection, which unites solid wood with supple aniline leather. The sofa is classically styled with rolled arms, brass nail-head trim, and front-facing bun feet. Premium spring-down cushions provide the perfect balance of softness and structure to the deep, comfortable seats.',
    dimensionsTable: [
      { label: 'Overall Dimensions', value: '36 1/3" H x 93 1/3" L x 40" D' },
      { label: 'Seat Width', value: '79"' },
      { label: 'Seat Depth', value: '24"' },
      { label: 'Seat Height', value: '18"' },
      { label: 'Arm Height', value: '24 1/3"' },
      { label: 'Weight', value: '200 lbs.' },
    ],
  },

  // ── Original Chair ─────────────────────────────────────────────────────────
  chair: {
    id: 'chair',
    name: 'Harwood Occasional Chair',
    subtitle: 'Ralph Lauren Home',
    tagline: 'Timeless elegance with handcrafted hardwood detail',
    heroImage: '/assets/ralph_images/chairs/demo_chair/chair_1.jpeg',
    tabs: ['fabric', 'finish', 'stud'],
    fabricZones: [
      { id: 'seat', label: 'Cushion', swatchKey: 'seatFabrics', paramKey: 'seatFabricUrl' },
      { id: 'back', label: 'Back', swatchKey: 'backCushionFabrics', paramKey: 'backCushionFabricUrl' },
      { id: 'armrest', label: 'Armrest', swatchKey: 'armrestFabrics', paramKey: 'armrestFabricUrl' },
    ],
    finishSwatchKey: 'woodFinishes',
    studConfig: {
      showSize: true,
      images: [
        { path: '/assets/ralph_input_images/stud.png', label: 'Brass Nailhead' },
        { path: '/assets/ralph_input_images/stud_2.png', label: 'Silver Nailhead' },
      ],
    },
    specs: [
      { label: 'Overall', value: '44" H x 27" L x 31" D' },
      { label: 'Weight', value: '50 lbs' },
    ],
    baseImages: [
      { path: '/assets/ralph_images/chairs/demo_chair/chair_1.jpeg', filename: 'chair_1.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_2.jpeg', filename: 'chair_2.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_3.jpeg', filename: 'chair_3.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_4.jpeg', filename: 'chair_4.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_5.jpeg', filename: 'chair_5.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_6.jpeg', filename: 'chair_6.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_chair/chair_7.jpeg', filename: 'chair_7.jpeg' },
    ],
    defaultDimensions: '44 " H x 27 " L x 31 " D',
    tearSheetImage: '/assets/ralph_dimensions/chair_tear_sheet.png',
    tearSheetTagline: 'HANDCRAFTED DETAILS',
    tearSheetDescription: 'This Biedermeier-style wing chair features an exposed wood frame with hand-carved arms and turned legs. The upholstered cushions are accented by classic nailhead trim.',
    dimensionsTable: [
      { label: 'Overall Dimensions', value: '44" H x 27" L x 31" D' },
      { label: 'Weight', value: '50 lbs.' },
    ],
  },

  // ── Demo 2 Sofa ────────────────────────────────────────────────────────────
  // All fabric zones share the same ralph-two-fabric endpoint.
  // Pillow: no size selection (default medium), fabric from ralph-two-fabric.
  sofa2: {
    id: 'sofa2',
    name: 'Modern Metropolis Sofa',
    subtitle: 'Ralph Lauren Home',
    tagline: 'Sophisticated two-fabric sofa with coordinated pillow styling',
    heroImage: '/assets/ralph_images/sofas/demo_2sofa/sofa1.jpeg',
    tabs: ['fabric', 'finish', 'pillow'],
    fabricZones: [
      { id: 'frame', label: 'Frame', swatchKey: 'ralphTwoFabric', paramKey: 'frameFabricUrl' },
      { id: 'seat', label: 'Cushion', swatchKey: 'ralphTwoFabric', paramKey: 'seatFabricUrl' },
      { id: 'back', label: 'Back', swatchKey: 'ralphTwoFabric', paramKey: 'backCushionFabricUrl' },
    ],
    finishSwatchKey: 'ralphSofaChairFinishes',
    pillowConfig: { showSize: false, showImageAndCount: false, fabricSwatchKey: 'ralphTwoFabric' },
    specs: [
      { label: 'Overall', value: '36" H x 90" L x 38" D' },
    ],
    baseImages: [
      { path: '/assets/ralph_images/sofas/demo_2sofa/sofa1.jpeg', filename: 'sofa1.jpeg' },
      { path: '/assets/ralph_images/sofas/demo_2sofa/sofa2.jpeg', filename: 'sofa2.jpeg' },
    ],
    defaultDimensions: '32" H x 96" L x 37 2/3" D, Seat width - 83, Seat depth - 27.5, Seat height - 20, Arm height - 25 1/3"',
    tearSheetImage: '/assets/ralph_dimensions/sofa2_tear_sheet.png',
    tearSheetTagline: 'SLEEK & STRIKING',
    tearSheetDescription: 'Exposed mahogany wood lends striking style to this American 1930s-inspired sofa, which is set upon a sleek plinth base and hand-upholstered with single welt trim matched with plush cushioning.',
    dimensionsTable: [
      { label: 'Overall Dimensions', value: '32" H x 96" L x 37 2/3" D' },
      { label: 'Seat Width', value: '83"' },
      { label: 'Seat Depth', value: '27.5"' },
      { label: 'Seat Height', value: '20"' },
      { label: 'Arm Height', value: '25 1/3"' },
      { label: 'Weight', value: '224 lbs.' },
    ],
  },

  // ── Demo 2 Chair ───────────────────────────────────────────────────────────
  // Each fabric zone has its own endpoint.
  // Stud: image only, no size selection (default M / medium).
  chair2: {
    id: 'chair2',
    name: 'Harwood Occasional Chair',
    subtitle: 'Ralph Lauren Home',
    tagline: 'Multi-fabric accent chair with nailhead detail',
    heroImage: '/assets/ralph_images/chairs/demo_2chair/chair_1.jpeg',
    tabs: ['fabric', 'finish', 'stud'],
    fabricZones: [
      { id: 'seat', label: 'Cushion', swatchKey: 'ralphChairSeatFabrics', paramKey: 'seatFabricUrl' },
      { id: 'back', label: 'Back', swatchKey: 'ralphChairBackFabrics', paramKey: 'backCushionFabricUrl' },
      { id: 'armrest', label: 'Armrest', swatchKey: 'ralphChairArmrestFabrics', paramKey: 'armrestFabricUrl' },
    ],
    finishSwatchKey: 'ralphSofaChairFinishes',
    studConfig: {
      showSize: false,
      images: [
        { path: '/assets/ralph_input_images/stud.png', label: 'Brass Nailhead' },
        { path: '/assets/ralph_input_images/stud_2.png', label: 'Silver Nailhead' },
      ],
    },
    specs: [
      { label: 'Overall', value: '44 " H x 27 " L x 31 " D' },
    ],
    baseImages: [
      { path: '/assets/ralph_images/chairs/demo_2chair/chair_1.jpeg', filename: 'chair_1.jpeg' },
      { path: '/assets/ralph_images/chairs/demo_2chair/chair_2.jpeg', filename: 'chair_2.jpeg' },
    ],
    defaultDimensions: '44 " H x 27 " L x 31 " D',
    tearSheetImage: '/assets/ralph_dimensions/chair_tear_sheet.png',
    tearSheetTagline: 'HANDCRAFTED DETAILS',
    tearSheetDescription: 'This Biedermeier-style wing chair features an exposed wood frame with hand-carved arms and turned legs. The upholstered cushions are accented by classic nailhead trim.',
    dimensionsTable: [
      { label: 'Overall Dimensions', value: '44 " H x 27 " L x 31 " D' },
      { label: 'Weight', value: '50 lbs.' },
    ],
  },
};

export const PRODUCT_ORDER: ProductId[] = ['sofa2', 'chair2', 'sofa', 'chair'];

export const TAB_CONFIG: Record<TabId, { label: string }> = {
  fabric: { label: 'FABRIC' },
  finish: { label: 'FINISH' },
  pillow: { label: 'PILLOW' },
  stud: { label: 'STUD' },
};
